import type { FullCar } from '@autobid/ui/types/Car'
import type { MaybeRef } from 'vue'
import { computed, unref } from 'vue'
import { useAuction } from '../../useAuction'
import { useCountdown } from '../../useCountdown'

type CountdownCar = Pick<
  FullCar,
  | 'auctionId'
  | 'bids'
  | 'hotbidStartTime'
  | 'pausedHotbidStartTime'
  | 'auctionStartDate'
  | 'inActiveHotbidPhase'
  | 'hotbidDuration'
>

export function useCarCountdownTarget(car: MaybeRef<CountdownCar | undefined | null>) {
  const { auction } = useAuction(computed(() => unref(car)?.auctionId))
  const targetTime = computed(() => {
    const _car = unref(car)
    if (!_car) {
      return new Date()
    }

    const lastBidTimestamp = _car.bids[0]?.timestamp

    const carTimestamp = (() => {
      if (auction.value?.isPaused && _car.pausedHotbidStartTime) {
        return _car.pausedHotbidStartTime
      }
      return _car.hotbidStartTime ?? _car.auctionStartDate
    })()

    // before hotbid
    if (!_car.inActiveHotbidPhase) {
      return new Date(carTimestamp)
    }

    const finalTimestamp
      = lastBidTimestamp && lastBidTimestamp > carTimestamp
        ? lastBidTimestamp
        : carTimestamp

    return new Date(
      new Date(
        new Date(finalTimestamp).getTime() + 1000 * _car.hotbidDuration.active,
      ).toISOString(),
    )
  })

  return {
    targetTime,
  }
}

export function useCarCountdown(car: MaybeRef<CountdownCar | undefined | null>) {
  const { targetTime } = useCarCountdownTarget(car)

  const { $socketTimeOffset } = useNuxtApp()
  const { auction } = useAuction(unref(car)?.auctionId)

  const overrideCurrentTime = computed(() => {
    if (!auction.value?.isPaused || !auction.value?.pauseDate) {
      return undefined
    }

    return new Date(auction.value.pauseDate)
  })

  return useCountdown(targetTime, $socketTimeOffset, overrideCurrentTime)
}
